import { logger } from "@clockwise/client-commons/src/util/logger";
import { Button } from "@clockwise/design-system";
import { useGatewayMutation } from "@clockwise/web-commons/src/network/apollo/gateway-provider";
import { TrackingEvents, useTracking } from "@clockwise/web-commons/src/util/analytics.util";
import React, { useEffect, useState } from "react";
import copyToClipboard from "../../../../../util/copyToClipboard";
import { SidebarLink } from "../../../../scheduling-link/LinksSection";
import { useShareProposal } from "../../../../shareable-proposal/hooks/useShareProposal";
import { useSetAIError } from "../../hooks/AIErrorContext";
import { CreateLinkFromDraftEventDocument } from "../proposal/__generated__/CreateLinkFromDraftEvent.v2.generated";
import { ShareProposalModal } from "./ShareProposalModal";

export const ProposalAvailability = ({
  disabled,
  canShareProposal,
  baseEventDiffId,
  proposalId,
}: {
  disabled: boolean;
  canShareProposal: boolean;
  baseEventDiffId: string;
  proposalId: string;
}) => {
  const track = useTracking();
  const setError = useSetAIError();
  const [modalOpen, setModalOpen] = useState(false);
  const [sharedProposalLink, setSharedProposalLink] = useState<string | null>(null);
  const [schedulingLink, setSchedulingLink] = useState<SidebarLink | null>(null);
  const [schedulingLinkLoading, setSchedulingLinkLoading] = useState(false);

  const [onShareProposal, { loading }] = useShareProposal({
    proposalId: proposalId,
    onError: (error) => {
      setError({
        error: error,
        message: "Failed to confirm proposal",
        showUserMessage: true,
      });
      logger.error(`Mutation error sharing link with propoalId - ${proposalId}`, error);
    },
    onCompleted: (data) => {
      if (data.shareProposal) {
        setSharedProposalLink(data.shareProposal.sharedProposalLink);
        setModalOpen(true);
      } else {
        const error = new Error("Data not returned from shareProposal mutation");
        setError({
          error: error,
          message: "Data not returned from shareProposal mutation",
          showUserMessage: true,
        });
        logger.error(`Mutation error sharing link with propoalId - ${proposalId}`, error);
      }
    },
  });

  const [getOrCreateLink] = useGatewayMutation(CreateLinkFromDraftEventDocument, {
    variables: { eventDiffId: baseEventDiffId },
    onError: (error) => {
      setError({
        error: error,
        message: "Oops. There was a problem creating the link. Please try again.",
        showUserMessage: true,
      });
      logger.error("Mutation error creating link via AI Scheduler", error);
      setSchedulingLink(null);
      setSchedulingLinkLoading(false);
    },
    onCompleted: (data) => {
      !modalOpen && setModalOpen(true);
      setSchedulingLink(data.createSchedulingLinkFromDraftEvent);
      setSchedulingLinkLoading(false);
    },
  });

  const onShareTimes = () => {
    track(TrackingEvents.SHAREABLE_PROPOSAL.SHARE_TIMES.BUTTON_CLICKED, { canShareProposal });

    if (canShareProposal) {
      void onShareProposal();
    } else {
      void getOrCreateLink();
    }
  };

  const onShareSchedulingLink = () => {
    if (!schedulingLink) {
      setSchedulingLinkLoading(true);
      void getOrCreateLink();
    }
  };

  useEffect(() => {
    track(TrackingEvents.SHAREABLE_PROPOSAL.SHARE_TIMES.BUTTON_VIEWED);
  }, []);

  return (
    <>
      <Button
        size="xsmall"
        sentiment="positive"
        variant="outlined"
        onClick={onShareTimes}
        disabled={disabled || loading}
      >
        Share times
      </Button>

      {modalOpen && (
        <ShareProposalModal
          canShareProposal={canShareProposal}
          sharedProposalLink={sharedProposalLink}
          schedulingLink={schedulingLink}
          onShareSchedulingLink={onShareSchedulingLink}
          schedulingLinkLoading={schedulingLinkLoading}
          onClose={() => setModalOpen(false)}
          onCopy={copyToClipboard}
        />
      )}
    </>
  );
};
